import { useSubscription } from "@/context/SubscriptionContext";
import NotIncludedInPlan from "@/components/NotIncludedInPlan";

/**
 * RequireEntitlement (M5 default-deny · 2026-07-10)
 *
 * Route-level guard that blocks children when the tenant's canonical
 * plan doesn't include the requested module/feature/workflow.
 *
 * Loading semantics changed with M5: while the entitlement snapshot is
 * still loading, we render a minimal loader — NEVER the protected
 * children — so a stale render never briefly shows an entitled screen
 * before flipping to the fallback. Once loading resolves, missing =
 * denied (no "legacy tenant → allow" fallback).
 *
 * The backend `require_module` / `require_feature` guards remain the
 * authority. This component only prevents obviously wasteful renders.
 */
export default function RequireEntitlement({
  module: moduleKey,
  feature: featureKey,
  workflow: workflowKey,
  fallback,
  children,
}) {
  const sub = useSubscription();
  if (!sub || sub.loading) return <_Loader />;

  if (moduleKey && !sub.hasModule(moduleKey))
    return fallback || <NotIncludedInPlan moduleKey={moduleKey} />;
  if (featureKey && !sub.hasFeature(featureKey))
    return fallback || <NotIncludedInPlan featureKey={featureKey} />;
  if (workflowKey && !sub.hasWorkflow(workflowKey))
    return fallback || <NotIncludedInPlan workflowKey={workflowKey} />;
  return children;
}

function _Loader() {
  return (
    <div
      className="min-h-[240px] flex items-center justify-center text-slate-500"
      data-testid="require-entitlement-loader"
    >
      <div className="h-5 w-5 rounded-full border-2 border-slate-300 border-t-cyan-600 animate-spin" />
    </div>
  );
}
